import {Modal, Select, TextInput} from '@mantine/core';
import {Box} from 'src/design/atoms/Box';
import {Divider} from 'src/design/atoms/Divider';
import {Group} from 'src/design/atoms/Group';
import {Text} from 'src/design/atoms/Text';
import React, {useEffect, useMemo} from 'react';
import {Controller, useForm} from 'react-hook-form';
import type {
  ConvertUserTypeRequest,
  PersonnelResponse} from 'src/api/ProactApiClient';
import {
  UserTypeEnum,
} from 'src/api/ProactApiClient';
import {Button} from 'src/design/atoms/Button';
import {useConvertUserTypeMutation} from 'src/redux/queries/personnel';
import {DialogButton} from 'src/design/molecules/DialogButton';
import {PersonSelectBox} from 'src/app/databound-elements/PersonSelectBox';
import {useGetVendorSelectOptionsQuery} from 'src/redux/queries/vendor';
import {useAlert} from 'src/shared/hooks/useAlert';
import {parseErrorMessage} from 'src/shared/utils/parseErrorMessage';
import type {ISelectOption} from 'src/types/Form';

export interface ConvertUserTypeModalProps {
  opened: boolean;
  onClose: () => void;
  item: PersonnelResponse | undefined;
  onSuccess: (response: PersonnelResponse) => void;
}

interface ConvertUserTypeFormValues {
  vendorId: string;
  employeeId: string;
  supervisorId: string;
}

export const ConvertUserTypeModal: React.FC<ConvertUserTypeModalProps> = ({
  opened,
  onClose,
  item,
  onSuccess,
}) => {
  const {setAlert} = useAlert();
  const [convertUserType, {isLoading}] = useConvertUserTypeMutation();

  const isToContractor = item?.userType === UserTypeEnum.Employee;
  const targetType = isToContractor
    ? UserTypeEnum.Contractor
    : UserTypeEnum.Employee;

  const {data: vendors} = useGetVendorSelectOptionsQuery(
    {start: 0, length: 10000, active: true},
    {skip: !isToContractor || !opened}
  );

  const vendorOptions = (vendors?.data ?? []).map(v => ({
    value: String(v.id),
    label: v.label ?? String(v.id),
  }));

  const defaultValues = useMemo<ConvertUserTypeFormValues>(
    () => ({
      vendorId: '',
      employeeId: '',
      supervisorId: item?.supervisorId != null ? String(item.supervisorId) : '',
    }),
    [item]
  );

  const {
    control,
    handleSubmit: handleFormSubmit,
    reset,
    formState: {isValid},
  } = useForm<ConvertUserTypeFormValues>({
    mode: 'onChange',
    defaultValues,
  });

  useEffect(() => {
    if (opened && item) {
      reset(defaultValues);
    }
  }, [opened, item, reset, defaultValues]);

  const onSubmit = async (values: ConvertUserTypeFormValues) => {
    if (!item) return;

    const request: ConvertUserTypeRequest = {
      targetUserType: targetType,
      vendorId:
        isToContractor && values.vendorId ? Number(values.vendorId) : undefined,
      employeeId: !isToContractor ? values.employeeId || undefined : undefined,
      supervisorId:
        !isToContractor && values.supervisorId
          ? Number(values.supervisorId)
          : undefined,
    };

    try {
      const response = await convertUserType({
        id: item.id,
        model: request,
      }).unwrap();
      setAlert('User type converted successfully.', 'success');
      onSuccess(response);
      onClose();
    } catch (error) {
      setAlert(parseErrorMessage(error), 'error');
    }
  };

  const title = isToContractor
    ? 'Convert to Contractor'
    : 'Convert to Employee';

  return (
    <Modal opened={opened} onClose={onClose} title={title} size="md" centered>
      <Text size="sm" c="dimmed" mb="md">
        {isToContractor
          ? 'This will convert the employee to a contractor. The employee ID will be cleared and the user will be assigned to the selected vendor.'
          : 'This will convert the contractor to an employee. The vendor association will be removed.'}
      </Text>

      <Divider mb="md" />

      <form onSubmit={handleFormSubmit(onSubmit)}>
        {isToContractor ? (
          <Box>
            <Controller
              name="vendorId"
              control={control}
              rules={{
                validate: value =>
                  (value && value !== '') || 'Vendor is required',
              }}
              render={({field}) => (
                <Select
                  label="Vendor"
                  placeholder="Select a vendor"
                  data={vendorOptions}
                  value={field.value || null}
                  onChange={val => field.onChange(val ?? '')}
                  onBlur={field.onBlur}
                  searchable
                  required
                  mb="md"
                />
              )}
            />
          </Box>
        ) : (
          <Box>
            <Controller
              name="employeeId"
              control={control}
              rules={{
                validate: value =>
                  (value ?? '').trim() !== '' || 'Employee ID is required',
              }}
              render={({field}) => (
                <TextInput
                  label="Employee ID"
                  placeholder="Enter employee ID"
                  {...field}
                  required
                  mb="md"
                />
              )}
            />
            <Box mb="md">
              <Text size="sm" fw={500} mb={4}>
                Supervisor
              </Text>
              <Text size="xs" c="dimmed" mb="xs">
                The supervisor is no longer managed by the vendor. Review or
                change it now.
              </Text>
              <Controller
                name="supervisorId"
                control={control}
                render={({field}) => (
                  <PersonSelectBox
                    onSelectChangeCallback={(value: ISelectOption | null) =>
                      field.onChange(value ? String(value.id) : '')
                    }
                    label=""
                    value={field.value || undefined}
                  />
                )}
              />
            </Box>
          </Box>
        )}

        <Divider mt="md" mb="md" />

        <Group justify="end">
          <DialogButton onClick={onClose}>Cancel</DialogButton>
          <Button type="submit" loading={isLoading} disabled={!isValid}>
            {title}
          </Button>
        </Group>
      </form>
    </Modal>
  );
};
